import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SupervisorLayout from '../../components/SupervisorLayout';
import { useSupervisorAuth } from '../../context/SupervisorAuthContext';
import { fetchSupervisorDashboard } from '../../api/supervisor';
import { todayISO } from '../../utils/date';
import './SupervisorDashboardPage.css';

// Grille d'icones du "Main Menu" superviseur (reference 2026-09-16, cf. memoire
// projet) — meme convention que DashboardPage.jsx (company) : la grille et le
// nav en dropdown (SupervisorLayout) menent aux memes pages, redondance
// volontaire deja presente cote company. Chaque tuile porte les menu_keys
// (SupervisorMenuPermission, 2026-09-17) dont AU MOINS UNE doit etre accordee
// pour que la tuile apparaisse (sinon masquee entierement, pas desactivee).
const menuItems = [
  {
    label: 'Central', icon: '🌐', path: '/supervisor/central',
    menuKeys: [
      '/supervisor/sales-monitoring', '/supervisor/banques-connecte', '/supervisor/verifier-ticket',
      '/supervisor/winning-numbers', '/supervisor/close-sales', '/supervisor/see-parameters',
      '/supervisor/express-monitoring', '/supervisor/change-terminal-password', '/supervisor/nullify-ticket',
      '/supervisor/revert-nullified-ticket', '/supervisor/pay-ticket',
    ],
  },
  {
    label: 'Rapports', icon: '📋', path: '/supervisor/rapports',
    menuKeys: [
      '/supervisor/general-report', '/supervisor/sales-list', '/supervisor/winning-numbers',
      '/supervisor/winning-tickets', '/supervisor/accumulated-summary', '/supervisor/cumul-detaille',
      '/supervisor/tickets-vendus', '/supervisor/tickets-annules', '/supervisor/resume-banque-lottery',
      '/supervisor/prized-numbers', '/supervisor/terminal-report', '/supervisor/bank-report',
    ],
  },
  {
    label: 'Controles', icon: '🎯', path: '/supervisor/controles',
    menuKeys: ['/supervisor/product-configs', '/supervisor/number-limits'],
  },
  {
    label: 'Parametres', icon: '⚙️', path: '/supervisor/parametres',
    menuKeys: ['/supervisor/banque', '/supervisor/act-desactiver-ventes-manuel'],
  },
];

const money = (n) => (n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const int = (n) => (n || 0).toLocaleString('en-US');

const Stat = ({ label, value, tone }) => (
  <div className={`svd-stat${tone ? ` svd-stat-${tone}` : ''}`}>
    <span className="svd-stat-label">{label}</span>
    <span className="svd-stat-value">{value}</span>
  </div>
);

const SupervisorDashboardPage = () => {
  const navigate = useNavigate();
  const { supervisor } = useSupervisorAuth();
  const [date] = useState(todayISO());
  const [ov, setOv] = useState(null);
  const [error, setError] = useState('');

  const granted = new Set(supervisor?.menu_keys || []);
  const visibleMenuItems = menuItems.filter((item) => item.menuKeys.some((k) => granted.has(k)));

  useEffect(() => {
    let cancelled = false;
    fetchSupervisorDashboard({ date })
      .then((d) => { if (!cancelled) setOv(d); })
      .catch(() => { if (!cancelled) setError('Impossible de charger le dashboard.'); });
    return () => { cancelled = true; };
  }, [date]);

  const m = ov?.money;

  return (
    <SupervisorLayout>
      <p className="svd-breadcrumb">
        Ruta: <Link to="/supervisor/dashboard" className="svd-breadcrumb-link">Menu</Link>
      </p>

      <div className="svd-icon-grid">
        {visibleMenuItems.map((item) => {
          const enabled = Boolean(item.path);
          return (
            <div
              key={item.label}
              className={`svd-icon-item${enabled ? '' : ' svd-icon-item-disabled'}`}
              onClick={enabled ? () => navigate(item.path) : undefined}
            >
              <div className="svd-icon-box">{item.icon}</div>
              <span className="svd-icon-label">{item.label}</span>
            </div>
          );
        })}
      </div>

      <h2 className="svd-title">Mon Dashboard</h2>

      {error && <p className="svd-error">{error}</p>}

      {ov && (
        <>
          <h3 className="svd-h">Aujourd'hui — {ov.date}</h3>
          <div className="svd-stat-grid">
            <Stat label="Total vendu" value={money(m.sales)} />
            <Stat label="Payout (gagné)" value={money(m.payout)} />
            <Stat label="Commission" value={money(m.commission)} />
            <Stat label="Profit / Marge" value={money(m.profit)} tone={m.profit < 0 ? 'warn' : 'ok'} />
            <Stat label="Fiches" value={int(m.tickets)} />
            <Stat label="Fiches gagnantes" value={int(m.winning_tickets)} />
          </div>

          <h3 className="svd-h">Mes Banks</h3>
          <div className="svd-stat-grid">
            <Stat label="Mes Groupes" value={int(ov.groups.total)} />
            <Stat label="POS / Agents" value={int(ov.pos.total)} />
            <Stat label="POS en ligne" value={int(ov.pos.online)} tone="ok" />
            <Stat label="POS hors ligne" value={int(ov.pos.offline)} tone={ov.pos.offline ? 'warn' : null} />
          </div>

          {ov.tirages.open.length > 0 && (
            <>
              <h3 className="svd-h">Tirages ouverts</h3>
              <ul className="svd-tirage-list">
                {ov.tirages.open.map((t) => (
                  <li key={t.code}>{t.code} — {t.name} ({t.start_time}–{t.end_time})</li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </SupervisorLayout>
  );
};

export default SupervisorDashboardPage;
